import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Mail, RefreshCw } from 'lucide-react';
import { useState } from 'react';

interface FormSubmittedSuccessProps {
  email: string;
  onResendEmail?: () => Promise<void>;
}

export const FormSubmittedSuccess: React.FC<FormSubmittedSuccessProps> = ({
  email,
  onResendEmail,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleResend = async () => {
    if (!onResendEmail) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      await onResendEmail();
      setResendSuccess(true);
    } catch (error) {
      console.error('Failed to resend email:', error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Contract Created Successfully!</CardTitle>
          <CardDescription>
            We've created your employment contract and sent you an email with the editor link.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Sent Confirmation */}
          <div className="bg-orange-50 border border-primary/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <h3 className="font-semibold mb-1 text-primary">Check Your Email</h3>
                <p className="text-sm mb-2 text-gray-700">
                  We've sent an email to <strong>{email}</strong> with a secure link to access your contract editor.
                </p>
                <p className="text-sm text-gray-700">
                  The link will expire in <strong>7 days</strong> for security reasons.
                </p>
              </div>
            </div>
          </div>

          {/* What to Expect */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">What happens next?</h3>
            <div className="space-y-2">
              <Step number={1} text="Check your email inbox (and spam folder)" />
              <Step number={2} text="Click the secure link to access your contract editor" />
              <Step number={3} text="Review the pre-filled contract" />
              <Step number={4} text="Use our AI assistant or manual editor to make any changes" />
              <Step number={5} text="Download your final contract as a PDF" />
            </div>
          </div>

          {/* Resend Email Option */}
          {onResendEmail && (
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600 mb-3">
                Didn't receive the email? Check your spam folder first, or request a new link.
              </p>
              <Button
                variant="outline"
                onClick={handleResend}
                disabled={isResending}
                className="w-full"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Email
                  </>
                )}
              </Button>
              {resendSuccess && (
                <p className="text-sm text-green-600 mt-2 text-center">
                  ✓ Email sent successfully!
                </p>
              )}
            </div>
          )}

          {/* Help Text */}
          <div className="pt-4 border-t text-center">
            <p className="text-sm text-gray-500">
              Need help? Contact us at <a href="mailto:support@nannypay.co.uk" className="text-blue-600 hover:underline">support@nannypay.co.uk</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for numbered steps
const Step: React.FC<{ number: number; text: string }> = ({ number, text }) => (
  <div className="flex items-center gap-3">
    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">
      {number}
    </div>
    <p className="text-sm text-gray-700">{text}</p>
  </div>
);

